import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { createAppleSsoSchema, AppleSsoFormData } from "../utils/ssoSchemas";
import { useConfigureAppleSso } from "../hooks/sso-settings.hooks";

interface AppleConfigDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantId: string;
}

export const AppleConfigDialog: React.FC<AppleConfigDialogProps> = ({
  open,
  onOpenChange,
  tenantId,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("sso.apple.dialogTitle")}</DialogTitle>
          <DialogDescription>
            {t("sso.apple.dialogDescription")}
          </DialogDescription>
        </DialogHeader>
        {open && (
          <AppleConfigForm
            tenantId={tenantId}
            onClose={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

const AppleConfigForm: React.FC<{
  tenantId: string;
  onClose: () => void;
}> = ({ tenantId, onClose }) => {
  const { t } = useTranslation();
  const { mutate: configure, isPending } = useConfigureAppleSso(tenantId);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AppleSsoFormData>({
    resolver: zodResolver(createAppleSsoSchema()),
    defaultValues: {
      clientId: "",
      teamId: "",
      keyId: "",
      privateKey: "",
    },
  });

  const onSubmit = (data: AppleSsoFormData) => {
    configure(data, {
      onSuccess: () => {
        toast.success(t("sso.apple.saveSuccess"));
        onClose();
      },
      onError: () => toast.error(t("sso.apple.saveError")),
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="apple-client-id">
          {t("sso.apple.clientIdLabel")} *
        </Label>
        <Input
          id="apple-client-id"
          placeholder={t("sso.apple.clientIdPlaceholder")}
          autoComplete="off"
          {...register("clientId")}
        />
        {errors.clientId ? (
          <p className="text-destructive text-xs">{errors.clientId.message}</p>
        ) : (
          <p className="text-muted-foreground text-xs">
            {t("sso.apple.clientIdHelp")}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="apple-team-id">{t("sso.apple.teamIdLabel")} *</Label>
          <Input
            id="apple-team-id"
            placeholder={t("sso.apple.teamIdPlaceholder")}
            autoComplete="off"
            {...register("teamId")}
          />
          {errors.teamId ? (
            <p className="text-destructive text-xs">{errors.teamId.message}</p>
          ) : (
            <p className="text-muted-foreground text-xs">
              {t("sso.apple.teamIdHelp")}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="apple-key-id">{t("sso.apple.keyIdLabel")} *</Label>
          <Input
            id="apple-key-id"
            placeholder={t("sso.apple.keyIdPlaceholder")}
            autoComplete="off"
            {...register("keyId")}
          />
          {errors.keyId ? (
            <p className="text-destructive text-xs">{errors.keyId.message}</p>
          ) : (
            <p className="text-muted-foreground text-xs">
              {t("sso.apple.keyIdHelp")}
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="apple-private-key">
          {t("sso.apple.privateKeyLabel")} *
        </Label>
        <Textarea
          id="apple-private-key"
          rows={6}
          placeholder={t("sso.apple.privateKeyPlaceholder")}
          className="font-mono text-xs"
          {...register("privateKey")}
        />
        {errors.privateKey ? (
          <p className="text-destructive text-xs">
            {errors.privateKey.message}
          </p>
        ) : (
          <p className="text-muted-foreground text-xs">
            {t("sso.apple.privateKeyHelp")}
          </p>
        )}
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button
          type="button"
          variant="new_outline"
          onClick={onClose}
          disabled={isPending}
        >
          {t("sso.actions.cancel")}
        </Button>
        <Button type="submit" variant="new_default" disabled={isPending}>
          {isPending ? t("sso.actions.saving") : t("sso.actions.save")}
        </Button>
      </div>
    </form>
  );
};
